import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR, { useSWRConfig } from 'swr';
import { AxiosError } from 'axios';
import {
  APPROVAL_V2_URL,
  getAnnualLeaveUrl,
  getOvertimeApplyUrl,
  getToilTimeAdjustmentUrl,
  getWorkingHoursHistoryUrl,
} from '@/constant/approval';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import {
  deleteDataFetcher,
  getNestedDataFetcher,
  putDataFetcher,
} from '@/utils/apis';
import { getCancelFormType } from '@/utils/approval/cancelApproval';
import openCancelApprovalCreate from '@/utils/approval/openCancelApprovalCreate';
import type { DocumentStatus } from '../../types';
import type {
  AnnualLeaveDetail,
  OvertimeApplyDetail,
  ToilTimeAdjustmentDetail,
  WorkingHoursHistory,
} from './types';

/**
 * 근태 자가결재 품의의 "품의 취소" (레거시 조회 폼 injectCancelButton 이식).
 * 노출 = 작성자 본인 + 결재완료(C) + 아직 취소 안 됨. 취소 가능 여부(cancelType)는 v1 상세가 판정:
 *  - immediate: 즉시 취소 API
 *  - approval : 취소 품의(226/227/232) 작성으로 이동 — 지난 근태는 결재를 거쳐 취소
 *  - none     : 버튼 비활성
 * 222 는 cancelType 없이 항상 즉시 취소(레거시 동일).
 * 32 연차휴가는 결재선을 타는 양식이지만 레거시도 231 과 같은 v1 상세·취소 품의 232 를 씀.
 */

type CancelType = 'immediate' | 'approval' | 'none';

type CancelSource = {
  alreadyCancelled: boolean;
  cancelType: CancelType;
  /** 즉시 취소 리소스 식별자. 222 만 별도 approvalId 를 씀 */
  resourceId: string | null;
};

const isCancelType = (v: unknown): v is CancelType =>
  v === 'immediate' || v === 'approval' || v === 'none';

const detailUrlOf = (apvType: number, documentId: string): string | null => {
  switch (apvType) {
    case 222:
      return getWorkingHoursHistoryUrl(documentId);
    case 223:
      return getOvertimeApplyUrl(documentId);
    case 224:
      return getToilTimeAdjustmentUrl(documentId);
    case 231:
    case 32:
      return getAnnualLeaveUrl(documentId);
    default:
      return null;
  }
};

const toCancelSource = (
  apvType: number,
  detail: unknown,
): CancelSource | null => {
  if (!detail) return null;
  switch (apvType) {
    case 222: {
      const d = detail as WorkingHoursHistory & {
        deletedAt?: string | null;
        approvalId?: string | null;
      };
      return {
        alreadyCancelled: !!d.deletedAt,
        cancelType: 'immediate',
        resourceId: d.approvalId ?? null,
      };
    }
    case 223: {
      const row = (detail as OvertimeApplyDetail).overtime as {
        deletedAt?: string | null;
        cancelType?: unknown;
      } | null;
      if (!row) return null;
      return {
        alreadyCancelled: !!row.deletedAt,
        cancelType: isCancelType(row.cancelType) ? row.cancelType : 'none',
        resourceId: null,
      };
    }
    case 224: {
      const row = (detail as ToilTimeAdjustmentDetail).toilTimeAdjustment as {
        deletedAt?: string | null;
        cancelType?: unknown;
      } | null;
      if (!row) return null;
      return {
        alreadyCancelled: !!row.deletedAt,
        cancelType: isCancelType(row.cancelType) ? row.cancelType : 'none',
        resourceId: null,
      };
    }
    case 231:
    case 32: {
      const d = detail as AnnualLeaveDetail & {
        isCancelled?: boolean;
        cancelType?: unknown;
      };
      return {
        alreadyCancelled: !!d.isCancelled,
        cancelType: isCancelType(d.cancelType) ? d.cancelType : 'none',
        resourceId: null,
      };
    }
    default:
      return null;
  }
};

type Params = {
  documentId: string;
  apvType?: number;
  status?: DocumentStatus;
  isAuthorMe: boolean;
  /** 즉시 취소 성공 후 호스트 목록 동기화(status X) */
  onCancelled?: () => void;
};

const useAttendanceCancel = ({
  documentId,
  apvType,
  status,
  isAuthorMe,
  onCancelled,
}: Params) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const { showSuccess, showError } = useSnackbar();
  const { mutate } = useSWRConfig();
  const [isCancelling, setIsCancelling] = useState(false);

  const eligible =
    isAuthorMe &&
    status === 'C' &&
    apvType !== undefined &&
    (apvType === 222 || getCancelFormType(apvType) !== undefined);
  const detailUrl =
    eligible && apvType !== undefined ? detailUrlOf(apvType, documentId) : null;

  // 슬롯(useAttendanceDetail)과 같은 key — 캐시 공유로 추가 요청 없음
  const { data: detail } = useSWR<unknown>(
    detailUrl,
    (key: string) => getNestedDataFetcher<unknown>(key),
    { revalidateOnFocus: false },
  );

  const source = useMemo(
    () => (apvType !== undefined ? toCancelSource(apvType, detail) : null),
    [apvType, detail],
  );

  const canCancel = eligible && !!source && !source.alreadyCancelled;
  const cancelDisabled = canCancel && source?.cancelType === 'none';

  // 재검증 실패는 무시 — 취소 API 성공 판정을 뒤집으면 안 됨
  const invalidate = useCallback(async () => {
    const baseKey = `${APPROVAL_V2_URL}/${documentId}`;
    await mutate(
      (cacheKey) =>
        cacheKey === baseKey ||
        (Array.isArray(cacheKey) && cacheKey[0] === baseKey) ||
        cacheKey === detailUrl,
    ).catch(() => undefined);
  }, [mutate, documentId, detailUrl]);

  const executeImmediate = useCallback(async () => {
    if (!apvType || !source || isCancelling) return;
    setIsCancelling(true);
    try {
      if (apvType === 222) {
        if (!source.resourceId) throw new Error('missing working-hours id');
        await deleteDataFetcher(
          `/api/v1/passport/working-hours/${source.resourceId}`,
        );
      } else {
        await putDataFetcher(`${APPROVAL_V2_URL}/${documentId}/cancel`, {
          approvalType: apvType,
        });
      }
      await invalidate();
      onCancelled?.();
      showSuccess({ message: t('품의가 취소되었습니다.') });
    } catch (error) {
      const httpStatus =
        error instanceof AxiosError ? error.response?.status : undefined;
      showError({
        message:
          httpStatus === 422
            ? t('이미 처리된 신청건은 취소가 불가능합니다.')
            : t('취소 처리 중 오류가 발생했습니다.'),
      });
    } finally {
      setIsCancelling(false);
    }
  }, [
    apvType,
    source,
    isCancelling,
    documentId,
    invalidate,
    onCancelled,
    showSuccess,
    showError,
    t,
  ]);

  /** 취소 품의 작성 화면 — 마이페이지 근태 서브메뉴와 같은 경로(V2/레거시 분기는 URL 헬퍼가 담당) */
  const moveToCancelApproval = useCallback(() => {
    if (!apvType) return;
    const formType = getCancelFormType(apvType);
    if (!formType) return;
    openCancelApprovalCreate(formType, documentId);
  }, [apvType, documentId]);

  const cancel = useCallback(() => {
    if (!canCancel || cancelDisabled || !source) return;
    if (source.cancelType === 'approval') {
      dialog.show({
        type: 'confirm',
        message: t('취소 품의를 작성하시겠습니까?'),
        confirmButtonText: t('취소 품의 작성'),
        cancelButtonText: t('닫기'),
        onConfirm: moveToCancelApproval,
      });
      return;
    }
    dialog.show({
      type: 'confirm',
      message:
        apvType === 222
          ? t('근무시간 변경 신청을 취소하시겠습니까?')
          : t('품의를 취소하시겠습니까?'),
      confirmButtonText: t('취소하기'),
      cancelButtonText: t('유지하기'),
      onConfirm: () => void executeImmediate(),
    });
  }, [
    canCancel,
    cancelDisabled,
    source,
    apvType,
    dialog,
    t,
    moveToCancelApproval,
    executeImmediate,
  ]);

  return { canCancel, cancelDisabled, cancel };
};

export default useAttendanceCancel;
